import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Target,
  X,
  Plus,
  Trash2,
  Copy,
  ListChecks,
  AlertTriangle,
  Info,

  Building2,
  Settings,
  Eye,
  Ear,
  Hand,
  Clock,
  Keyboard,
  Image as ImageIcon,
  ToggleLeft,
  ToggleRight,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  CalendarDays,
  CalendarRange,
  Calendar,
  CalendarCheck,
  Repeat,
  History,
  LayoutGrid,
  PieChart
} from 'lucide-react';
import {
  BulkCreateCheckpointForm,

  ProjectType as CheckpointProjectType
} from '../../../types/checkpoint';
import { FREQUENCY_OPTIONS } from '../../../types/checklist';
import { Asset } from '../../../types/asset';
import projectTypeService from '../../../services/projectTypeService';
import { assetService } from '../../../services/assetService';
import { checkpointService } from '../../../services/checkpointService';
import Select from '../../../shared/components/form/Select';
import Button from '../../../shared/components/ui/button/Button';
import PageBreadcrumb from '../../../shared/PageBreadCrumb';
import { getDefaultBufferTime, getBufferTimeOptions, getMaxBufferTime } from '../utils/checkpointUtils';

// Modern Clean Brand Colors - Matching UserForm.tsx
const BRAND_COLORS = {
  primary: "#339274",          // Primary Green
  primaryHover: "#29795f",     // Darker Green for hover
  primaryLight: "#F8FCFA",     // Very subtle green background
  primarySoft: "#EDF7F2",      // Soft green background
  accent: "#A3CD39",           // Lime accent
  dark: "#007F66",             // Deep teal
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

// Validation schema for bulk form
const schema = yup.object({
  projectTypeId: yup.string()
    .required('Project Type is required'),
  assetId: yup.string()
    .required('Asset is required'),
  items: yup.array().of(
    yup.object({
      name: yup.string()
        .required('Checkpoint name is required')
        .min(2, 'Name must be at least 2 characters')
        .max(200, 'Name must be less than 200 characters'),
      description: yup.string()
        .optional()
        .max(2000, 'Description must be less than 2000 characters'),
      frequency: yup.string()
        .required('Frequency is required')
        .oneOf(['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly']),
      bufferTimeDays: yup.number()
        .optional()
        .min(0, 'Grace period cannot be negative')
        .integer('Grace period must be a whole number')
        .test(
          'max-buffer-for-frequency',
          function (value) {
            const { frequency } = this.parent as { frequency?: string };
            if (value === undefined || value === null) return true;
            if (!frequency) return true;

            const maxBuffer = getMaxBufferTime(frequency as any);
            const isValid = value <= maxBuffer;

            if (!isValid) {
              const frequencyDisplay = frequency.replace('-', ' ');
              return this.createError({
                message: `Grace period cannot exceed ${maxBuffer} day${maxBuffer !== 1 ? 's' : ''} for ${frequencyDisplay} frequency`
              });
            }

            return true;
          }
        ),
      requirement: yup.string()
        .optional()
        .max(1000, 'Requirement must be less than 1000 characters'),
    })
  ).min(1, 'At least one checkpoint is required'),
});

type FormValues = BulkCreateCheckpointForm;

export default function BulkCheckpointFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ========== Form State ==========
  const [projectTypes, setProjectTypes] = useState<CheckpointProjectType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [allAssets, setAllAssets] = useState<Asset[]>([]); // Store all assets for search
  const [assetSearchQuery, setAssetSearchQuery] = useState(''); // Search query for assets
  const [isLoadingProjectTypes, setIsLoadingProjectTypes] = useState(true);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showWarning, setShowWarning] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid, isDirty }
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange" as const,
    defaultValues: {
      projectTypeId: '',
      assetId: '',
      items: [
        {
          name: '',
          frequency: 'Weekly' as const,
          bufferTimeDays: getDefaultBufferTime('Weekly'), // Default buffer time
          requirement: '', // Optional requirement field
          isInputRequired: 'None' as any, // Default to None
          status: 'active' as const,
        }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items"
  });

  const watchedProjectTypeId = watch('projectTypeId');
  const watchedAssetId = watch('assetId');
  const watchedItems = watch('items');

  // ========== Effects ==========

  // Fetch project types on mount
  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        setIsLoadingProjectTypes(true);
        const response = await projectTypeService.getAllProjectTypes();
        const activeProjectTypes = response.filter(pt => pt.status === 'active');

        // Convert project types to match checkpoint interface
        const convertedProjectTypes: CheckpointProjectType[] = activeProjectTypes.map(pt => ({
          _id: pt._id,
          name: pt.name,
          description: pt.description || '',
          status: pt.status,
          sequence: pt.sequence || 0,
          displayNumber: pt.displayNumber || '',
          createdAt: pt.createdAt || '',
          updatedAt: pt.updatedAt || '',
          // Handle createdBy and updatedBy as they might be strings or User objects
          createdBy: typeof pt.createdBy === 'string' ? undefined : pt.createdBy,
          updatedBy: typeof pt.updatedBy === 'string' ? undefined : pt.updatedBy,
        }));

        setProjectTypes(convertedProjectTypes);

        // Set initial project type from URL params if available
        const urlProjectTypeId = searchParams.get('projectTypeId');
        if (urlProjectTypeId && convertedProjectTypes.some(pt => pt._id === urlProjectTypeId)) {
          setValue('projectTypeId', urlProjectTypeId);
        }
      } catch (error) {
        console.error('Failed to fetch project types:', error);
        toast.error('Failed to load project types');
      } finally {
        setIsLoadingProjectTypes(false);
      }
    };

    fetchProjectTypes();
  }, [searchParams, setValue]);

  // Fetch assets when project type changes - GET ALL ASSETS
  useEffect(() => {
    const fetchAssets = async () => {
      if (!watchedProjectTypeId) {
        setAssets([]);
        setAllAssets([]);
        setValue('assetId', '');
        setAssetSearchQuery(''); // Reset search
        return;
      }

      try {
        setIsLoadingAssets(true);
        // Ensure projectTypeId is a string
        const projectTypeId = typeof watchedProjectTypeId === 'string' ? watchedProjectTypeId : watchedProjectTypeId;

        // Fetch ALL assets with limit=100 (backend max)
        const response = await assetService.getAssetsByProjectType(projectTypeId, { limit: 100 });
        console.log('Assets fetched:', response);

        // Handle the new response structure
        let assetsData: Asset[] = [];
        if (response && typeof response === 'object' && 'assets' in response) {
          assetsData = response.assets;
        } else if (Array.isArray(response)) {
          assetsData = response;
        } else if (response && typeof response === 'object' && 'data' in response) {
          const data = (response as any).data;
          if (Array.isArray(data)) {
            assetsData = data;
          } else if (data && Array.isArray(data.docs)) {
            assetsData = data.docs;
          }
        }

        const activeAssets = assetsData.filter(asset => asset.status === 'active');

        // Store all assets for search/filter
        setAllAssets(activeAssets);
        setAssets(activeAssets);

        // Set initial asset from URL params if available
        const urlAssetId = searchParams.get('assetId');
        if (urlAssetId && activeAssets.some(asset => asset._id === urlAssetId)) {
          setValue('assetId', urlAssetId);
        } else {
          setValue('assetId', '');
        }
      } catch (error) {
        console.error('Failed to fetch assets:', error);
        toast.error('Failed to load assets');
        setAssets([]);
        setAllAssets([]);
      } finally {
        setIsLoadingAssets(false);
      }
    };

    fetchAssets();
  }, [watchedProjectTypeId, searchParams, setValue]);

  // Filter assets based on search query
  useEffect(() => {
    if (!assetSearchQuery.trim()) {
      setAssets(allAssets);
      return;
    }

    const query = assetSearchQuery.toLowerCase().trim();
    const filtered = allAssets.filter(asset =>
      asset.name?.toLowerCase().includes(query) ||
      asset.code?.toLowerCase().includes(query) ||
      asset.description?.toLowerCase().includes(query)
    );

    setAssets(filtered);
  }, [assetSearchQuery, allAssets]);

  // Show warning when adding many checkpoints
  useEffect(() => {
    if (fields.length > 10) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  }, [fields.length]);

  // ========== Event Handlers ==========

  const handleProjectTypeChange = async (projectTypeId: string) => {
    setValue('projectTypeId', projectTypeId);
    setValue('assetId', ''); // Reset asset when project type changes
  };

  const handleAssetChange = (assetId: string) => {
    setValue('assetId', assetId);
  };

  const addCheckpoint = () => {
    append({
      name: '',
      frequency: 'Weekly' as const,
      bufferTimeDays: getDefaultBufferTime('Weekly'), // Default buffer time
      requirement: '', // Optional requirement field
      status: 'active' as const,
    });

    // Focus on the new checkpoint name field
    setTimeout(() => {
      const newIndex = fields.length;
      const input = document.querySelector(`input[name="items.${newIndex}.name"]`) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  };

  const removeCheckpoint = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      toast.success('Checkpoint removed');
    } else {
      toast.warning('At least one checkpoint is required');
    }
  };

  const duplicateCheckpoint = (index: number) => {
    const checkpointToDuplicate = fields[index];
    const currentInputRequired = watchedItems[index]?.isInputRequired;
    // Keep the same value (Both/Number/Text/None) when duplicating

    append({
      name: `${checkpointToDuplicate.name} (Copy)`,
      frequency: checkpointToDuplicate.frequency,
      bufferTimeDays: checkpointToDuplicate.bufferTimeDays || getDefaultBufferTime(checkpointToDuplicate.frequency),
      requirement: checkpointToDuplicate.requirement || '', // Copy requirement field
      isInputRequired: currentInputRequired || 'None' as any,
      status: checkpointToDuplicate.status,
    });
    toast.success('Checkpoint duplicated');
  };

  const onSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      setApiError(null);

      // Map input required values: Both/Number/Text -> true, None/undefined -> false
      // Map input required values: Both/Number/Text -> true, None/undefined -> false
      const mappedData: BulkCreateCheckpointForm = {
        ...data,
        items: data.items.map(item => {
          // Explicitly separate isInputRequired to ensure we don't spread the string value "None"
          const { isInputRequired, ...otherProps } = item;
          // Cast to any because the runtime value is a string (from the form) but the type is boolean
          const val = isInputRequired as any;
          const mappedInputRequired = (val === 'Both' || val === 'Number' || val === 'Text') ? 'Both' : 'None';

          return {
            ...otherProps,
            isInputRequired: mappedInputRequired as any,
          };
        })
      };

      const response = await checkpointService.createBulkCheckpoints(mappedData);

      if (response.success) {
        setShowSuccess(true);
        // toast.success(`Successfully created ${response.data.totalCreated} checkpoints`);

        // Navigate back to checkpoints list after a short delay
        setTimeout(() => {
          navigate('/masters/checkpoints');
        }, 2000);
      } else {
        setApiError(response.message || 'Failed to create checkpoints');
        toast.error(response.message || 'Failed to create checkpoints');
      }
    } catch (error: any) {
      console.error('Failed to create checkpoints:', error);
      setApiError(error.message || 'Failed to create checkpoints');
      toast.error(error.message || 'Failed to create checkpoints');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    reset({
      projectTypeId: watchedProjectTypeId,
      assetId: watchedAssetId,
      items: [
        {
          name: '',
          frequency: 'Weekly' as const,
          bufferTimeDays: getDefaultBufferTime('Weekly'),
          requirement: '', // Optional requirement field
          isInputRequired: 'None' as any, // Default to None
          status: 'active' as const,
        }
      ]
    });
    setApiError(null);
    toast.info('Form has been reset');
  };

  const handleCancel = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/masters/checkpoints');
      }
    } else {
      navigate('/masters/checkpoints');
    }
  };

  // ========== Render ==========

  if (showSuccess) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: `linear-gradient(120deg, ${BRAND_COLORS.primarySoft} 0%, #f8fafc 100%)`,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 120, damping: 18 }}
          className="w-full max-w-lg mx-4"
        >
          <div className="flex flex-col items-center">
            {/* Animated Prime Checkmark */}
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.1 }}
              className="mb-8"
            >
              <motion.svg
                width="80"
                height="80"
                viewBox="0 0 80 80"
                className="drop-shadow-lg"
              >
                <motion.circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  stroke={BRAND_COLORS.primary}
                  strokeWidth="4"
                  initial={{ pathLength: 0, opacity: 0.2 }}
                  animate={{ pathLength: 1, opacity: 0.15 }}
                  transition={{ duration: 0.7, delay: 0.1, ease: "easeInOut" }}
                />
                <motion.circle
                  cx="40"
                  cy="40"
                  r="32"
                  fill={BRAND_COLORS.primary}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 0.12 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                />
                <motion.path
                  d="M26 42L37 53L56 30"
                  fill="none"
                  stroke={BRAND_COLORS.primary}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.6, delay: 0.35, ease: "easeInOut" }}
                />
              </motion.svg>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-2xl font-bold text-[#222] mb-2 tracking-tight"
              style={{
                letterSpacing: "-0.01em",
                color: BRAND_COLORS.primary,
              }}
            >
              Checkpoints Created
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="mt-6"
            >
              <span
                className="inline-block px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  background: BRAND_COLORS.primaryLight,
                  color: BRAND_COLORS.primary,
                  border: `1px solid ${BRAND_COLORS.primary}`,
                  letterSpacing: "0.01em",
                }}
              >
                You can now manage these checkpoints in your asset list.
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 py-4 mx-auto max-w-[1920px]">
        <PageBreadcrumb
          items={[
            { name: "Masters", path: "/masters" },
            { name: "Checkpoints", path: "/masters/checkpoints" },
            { name: "Bulk Create", path: "/masters/checkpoints/bulk" },
          ]}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          {/* Header - Modern Clean Style */}
          <div className="px-6 py-4 border-b border-gray-200"
            style={{ backgroundColor: BRAND_COLORS.primaryLight }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
                  style={{
                    background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                    borderColor: BRAND_COLORS.primary
                  }}
                >
                  <ListChecks className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    Bulk Create Checkpoints
                  </h1>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Efficiently create multiple checkpoints for your assets
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="font-medium h-9 px-4 text-xs border rounded-lg"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-2" />
                Cancel
              </Button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit as any)} className="p-4 space-y-4">
            {/* Project & Asset Selection - Compact & Prime */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">

              {/* Project Type Selection - Horizontal Strip */}
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2 whitespace-nowrap min-w-fit">
                  <Building2 className="w-3.5 h-3.5" />
                  Project Type
                </label>

                <div className="flex-1 flex flex-wrap gap-2">
                  {isLoadingProjectTypes ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Loading...
                    </div>
                  ) : (
                    projectTypes.map((pt) => {
                      const isSelected = watchedProjectTypeId === pt._id;
                      return (
                        <button
                          key={pt._id}
                          type="button"
                          onClick={() => {
                            setValue('projectTypeId', pt._id, { shouldDirty: true, shouldValidate: true });
                            handleProjectTypeChange(pt._id);
                          }}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all duration-200 ${isSelected
                            ? "border-green-500 bg-white text-green-700 shadow-sm ring-1 ring-green-500/20"
                            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                          {pt.name}
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                        </button>
                      );
                    })
                  )}
                </div>

                {errors.projectTypeId && (
                  <div className="flex items-center gap-1.5 text-xs text-red-600 md:ml-auto">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span className="font-medium">Required</span>
                  </div>
                )}
              </div>

              {/* Asset Selection - Compact Grid */}
              <div className="p-5">
                {watchedProjectTypeId ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                        <Target className="w-3.5 h-3.5" />
                        Select Asset
                        <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[10px] font-bold">
                          {assets.length} {assets.length !== allAssets.length && `/ ${allAssets.length}`}
                        </span>
                      </label>
                      {isLoadingAssets && <Loader2 className="w-3.5 h-3.5 animate-spin text-green-600" />}
                    </div>

                    {/* Search Box */}
                    {allAssets.length > 0 && (
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search assets by name or code..."
                          value={assetSearchQuery}
                          onChange={(e) => setAssetSearchQuery(e.target.value)}
                          className="w-full px-3 py-2 pl-9 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all"
                          style={{ fontSize: '13px' }}
                        />
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        {assetSearchQuery && (
                          <button
                            type="button"
                            onClick={() => setAssetSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}

                    {assets.length > 0 ? (
                      <>
                        {assetSearchQuery && (
                          <p className="text-xs text-gray-500">
                            {assets.length === allAssets.length
                              ? `Showing all ${assets.length} assets`
                              : `Found ${assets.length} of ${allAssets.length} assets`
                            }
                          </p>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[280px] overflow-y-auto pr-1 custom-scrollbar">
                          {assets.map((asset) => {
                            const isSelected = watchedAssetId === (asset.id || asset._id);
                            return (
                              <button
                                key={asset.id || asset._id}
                                type="button"
                                onClick={() => {
                                  const assetId = asset.id || asset._id;
                                  setValue('assetId', assetId, { shouldDirty: true, shouldValidate: true });
                                  handleAssetChange(assetId);
                                }}
                                className={`group relative flex flex-col p-2.5 rounded-lg border transition-all duration-200 text-left ${isSelected
                                  ? "border-green-500 bg-green-50/30 ring-1 ring-green-500/20 z-10"
                                  : "border-gray-100 bg-white hover:border-green-300 hover:shadow-sm"
                                  }`}
                              >
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <Settings className={`w-3.5 h-3.5 ${isSelected ? 'text-green-600' : 'text-gray-400 group-hover:text-green-500'}`} />
                                  {isSelected && <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />}
                                </div>
                                <span className={`text-xs font-bold truncate w-full ${isSelected ? "text-gray-900" : "text-gray-700"}`}>
                                  {asset.name}
                                </span>
                                <span className="text-[10px] text-gray-400 truncate w-full">
                                  {asset.code || 'No Code'}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        <p className="text-xs text-gray-500">
                          {assetSearchQuery
                            ? `No assets found matching "${assetSearchQuery}"`
                            : 'No assets found for this project type'
                          }
                        </p>
                      </div>
                    )}

                    {errors.assetId && (
                      <p className="text-xs text-red-600 flex items-center gap-1 mt-2">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Please select an asset
                      </p>
                    )}
                  </motion.div>
                ) : (
                  <div className="py-8 text-center bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
                    <p className="text-sm font-medium text-gray-400">Select a Project Type to view assets</p>
                  </div>
                )}
              </div>
            </div>

            {/* Information Note - Clean Style */}
            {/* <div className="p-4 rounded-lg border" 
              style={{ 
                backgroundColor: BRAND_COLORS.primaryLight,
                borderColor: BRAND_COLORS.primary + '30'
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center border"
                  style={{ 
                    backgroundColor: BRAND_COLORS.primary + '20',
                    borderColor: BRAND_COLORS.primary + '40'
                  }}
                >
                  <Info size={16} style={{ color: BRAND_COLORS.primary }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: BRAND_COLORS.dark }}>
                    Bulk Creation Benefits
                  </p>
                  <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                    Create multiple checkpoints efficiently with consistent settings. Each checkpoint will be assigned to the selected asset and project type. 
                    All checkpoints are created with 'active' status by default. Use the duplicate feature to quickly copy existing configurations.
                  </p>
                </div>
              </div>
            </div> */}

            {/* Warning Alert - Clean Style */}
            <AnimatePresence>
              {showWarning && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-3 p-4 rounded-lg border"
                  style={{
                    backgroundColor: BRAND_COLORS.warningLight,
                    borderColor: `${BRAND_COLORS.warning}40`
                  }}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
                    style={{
                      backgroundColor: BRAND_COLORS.warning + '20',
                      borderColor: BRAND_COLORS.warning + '40'
                    }}
                  >
                    <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.warning }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.warning }}>
                      Large Number of Checkpoints
                    </p>
                    <p className="text-xs mt-1" style={{ color: BRAND_COLORS.warning }}>
                      You're adding {fields.length} checkpoints. Consider breaking this into smaller batches for better performance and easier management.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowWarning(false)}
                    className="p-1.5 rounded-lg hover:bg-yellow-100 transition-colors"
                    style={{ color: BRAND_COLORS.warning }}
                  >
                    <X size={16} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Checkpoints List - Modern Card Layout */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Checkpoints
                  </h3>
                  <span className="px-3 py-1 text-xs font-medium rounded-full"
                    style={{
                      backgroundColor: BRAND_COLORS.primary + '20',
                      color: BRAND_COLORS.primary
                    }}
                  >
                    {fields.length} {fields.length === 1 ? 'checkpoint' : 'checkpoints'}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCheckpoint}
                  className="font-medium h-9 px-4 text-xs border rounded-lg border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Plus className="w-3.5 h-3.5 mr-2" />
                  Add Checkpoint
                </Button>
              </div>

              <AnimatePresence>
                {fields.map((field, index) => (
                  <motion.div
                    key={field.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center border"
                          style={{
                            backgroundColor: BRAND_COLORS.primary + '20',
                            borderColor: BRAND_COLORS.primary + '40'
                          }}
                        >
                          <Settings className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                        </div>
                        <h4 className="text-sm font-medium text-gray-700">
                          Checkpoint {index + 1}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => duplicateCheckpoint(index)}
                          className="text-blue-600 border-blue-600 hover:bg-blue-50 font-medium h-8 px-3 text-xs rounded-lg"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Duplicate
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeCheckpoint(index)}
                          className="text-red-600 border-red-600 hover:bg-red-50 font-medium h-8 px-3 text-xs rounded-lg"
                          disabled={fields.length === 1}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Top Row: Description & Requirement */}
                      <div className="flex flex-col lg:flex-row gap-4">
                        {/* Checkpoint Description (Name) */}
                        <div className="flex-[2] space-y-1.5">
                          <label className="block text-sm font-medium text-gray-900">
                            Checkpoint Description *
                          </label>
                          <div className="relative">
                            <textarea
                              {...register(`items.${index}.name`)}
                              placeholder="Enter checkpoint description..."
                              rows={3}
                              className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 resize-y min-h-[80px] ${errors.items?.[index]?.name
                                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                                : watchedItems?.[index]?.name && !errors.items?.[index]?.name
                                  ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                                  : "border-gray-300 hover:border-gray-400 focus:border-green-500 focus:ring-green-500/20"
                                }`}
                              style={!errors.items?.[index]?.name ? {
                                '--tw-ring-color': BRAND_COLORS.primary + '40'
                              } as React.CSSProperties : {}}
                            />
                            {watchedItems?.[index]?.name && !errors.items?.[index]?.name && (
                              <CheckCircle2 className="absolute right-3 top-3 w-4 h-4"
                                style={{ color: BRAND_COLORS.primary }} />
                            )}
                            {errors.items?.[index]?.name && (
                              <AlertCircle className="absolute right-3 top-3 w-4 h-4 text-red-500" />
                            )}
                          </div>
                          {errors.items?.[index]?.name && (
                            <p className="flex items-center gap-1.5 text-xs text-red-600">
                              <AlertCircle className="w-3 h-3 flex-shrink-0" />
                              {errors.items[index]?.name?.message}
                            </p>
                          )}
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>Min: 2 chars, Max: 200 chars</span>
                            <span className={watchedItems?.[index]?.name?.length > 200 ? 'text-red-600 font-semibold' : watchedItems?.[index]?.name?.length > 180 ? 'text-orange-600' : ''}>
                              {watchedItems?.[index]?.name?.length || 0}/200
                            </span>
                          </div>
                        </div>

                        {/* Requirement */}
                        <div className="flex-1 space-y-1.5">
                          <label className="block text-sm font-medium text-gray-900">
                            Requirement <span className="text-gray-500 text-xs">(Optional)</span>
                          </label>
                          <div className="relative">
                            <textarea
                              {...register(`items.${index}.requirement`)}
                              placeholder="Requirement details..."
                              rows={3}
                              className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 resize-y min-h-[80px] ${errors.items?.[index]?.requirement
                                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                                : "border-gray-300 hover:border-gray-400 focus:border-green-500 focus:ring-green-500/20"
                                }`}
                              style={!errors.items?.[index]?.requirement ? {
                                '--tw-ring-color': BRAND_COLORS.primary + '40'
                              } as React.CSSProperties : {}}
                            />
                          </div>
                          {errors.items?.[index]?.requirement && (
                            <p className="flex items-center gap-1.5 text-xs text-red-600">
                              <AlertCircle className="w-3 h-3 flex-shrink-0" />
                              {errors.items[index]?.requirement?.message}
                            </p>
                          )}
                          <div className="flex justify-end text-xs text-gray-500">
                            <span className={watchedItems?.[index]?.requirement?.length > 1000 ? 'text-red-600 font-semibold' : watchedItems?.[index]?.requirement?.length > 900 ? 'text-orange-600' : ''}>
                              {watchedItems?.[index]?.requirement?.length || 0}/1000
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Control Board Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 rounded-xl border border-gray-100 bg-gradient-to-b from-white to-gray-50/50 shadow-sm relative overflow-hidden group hover:shadow-md transition-all duration-300">

                        {/* Decorative Background Element */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-green-50/50 to-transparent rounded-bl-[4rem] -z-0" />

                        {/* Panel 1: Scheduling (Frequency & Buffer) */}
                        <div className="space-y-3 relative z-10">
                          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                            <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center text-green-600">
                              <Calendar className="w-3.5 h-3.5" />
                            </div>
                            <h5 className="text-xs font-bold text-gray-900">Scheduling</h5>
                          </div>

                          {/* Frequency - Compact */}
                          <div className="space-y-1.5">
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Frequency</label>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                              {[
                                { id: 'Daily', label: 'Daily' },
                                { id: 'Weekly', label: 'Weekly' },
                                { id: 'Fortnightly', label: '2-Weeks' },
                                { id: 'Monthly', label: 'Monthly' },
                                { id: 'Quarterly', label: 'Quarterly' },
                                { id: 'Half-Yearly', label: '6-Months' },
                                { id: 'Yearly', label: 'Yearly' },
                              ].map((freq) => {
                                const isSelected = watchedItems[index]?.frequency === freq.id;
                                return (
                                  <button
                                    key={freq.id}
                                    type="button"
                                    onClick={() => {
                                      setValue(`items.${index}.frequency`, freq.id as any, { shouldDirty: true });
                                      const defaultBufferTime = getDefaultBufferTime(freq.id as any);
                                      setValue(`items.${index}.bufferTimeDays`, defaultBufferTime, { shouldDirty: true });
                                    }}
                                    className={`px-3 py-2 rounded-lg text-[11px] font-semibold transition-all duration-200 border transform active:scale-95 ${isSelected
                                      ? "bg-green-600 text-white border-green-600 shadow-md shadow-green-200"
                                      : "bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:text-green-700 hover:shadow-sm"
                                      }`}
                                  >
                                    {freq.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Execution Phase (Buffer) - Compact */}
                          <div className="space-y-1.5">
                            <div className="flex justify-between items-end">
                              <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Buffer Days</label>
                              <span className="text-[10px] font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-100">
                                Max: {getMaxBufferTime(watchedItems[index]?.frequency || 'Weekly')}d
                              </span>
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-2 w-full">
                                <div className="flex items-center bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden h-9">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const current = watchedItems[index]?.bufferTimeDays || 0;
                                      if (current > 0) setValue(`items.${index}.bufferTimeDays`, current - 1, { shouldDirty: true });
                                    }}
                                    className="w-9 h-full flex items-center justify-center hover:bg-gray-50 text-gray-500 border-r border-gray-100 transition-colors"
                                  >-</button>
                                  <span className="w-10 text-center text-sm font-bold text-gray-700">{watchedItems[index]?.bufferTimeDays || 0}d</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const current = watchedItems[index]?.bufferTimeDays || 0;
                                      const max = getMaxBufferTime(watchedItems[index]?.frequency || 'Weekly');
                                      if (current < max) setValue(`items.${index}.bufferTimeDays`, current + 1, { shouldDirty: true });
                                    }}
                                    className="w-9 h-full flex items-center justify-center hover:bg-gray-50 text-gray-500 border-l border-gray-100 transition-colors"
                                  >+</button>
                                </div>

                                {/* Quick Select Pills - Horizontal Scroll */}
                                <div className="flex-1 flex gap-1.5 overflow-x-auto hide-scrollbar py-1">
                                  {[0, 1, 2, 3, 5, 7, 14, 30].map((days) => {
                                    const max = getMaxBufferTime(watchedItems[index]?.frequency || 'Weekly');
                                    if (days > max) return null;
                                    return (
                                      <button
                                        key={days}
                                        type="button"
                                        onClick={() => setValue(`items.${index}.bufferTimeDays`, days, { shouldDirty: true })}
                                        className={`flex-shrink-0 w-8 h-8 rounded-lg text-[11px] font-bold border transition-all duration-200 flex items-center justify-center ${watchedItems[index]?.bufferTimeDays === days
                                          ? "bg-green-100 text-green-700 border-green-200 ring-2 ring-green-500/20"
                                          : "bg-white text-gray-500 border-gray-200 hover:border-green-300 hover:text-green-600 hover:shadow-sm"
                                          }`}
                                      >
                                        {days}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Panel 2: Mode of Execution */}
                        <div className="space-y-3 relative z-10">
                          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                            <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                              <ListChecks className="w-3.5 h-3.5" />
                            </div>
                            <h5 className="text-xs font-bold text-gray-900">Mode of Execution</h5>
                          </div>

                          {/* Task Type */}
                          <div className="space-y-1.5">
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Task Type</label>
                            <div className="flex flex-col gap-1.5">
                              {[
                                { id: 'Visual', icon: Eye, label: 'Visual Check', color: 'blue' },
                                { id: 'Hearing', icon: Ear, label: 'Auditory Check', color: 'indigo' },
                                { id: 'Manual', icon: Hand, label: 'Manual Action', color: 'amber' },
                              ].map((type) => {
                                const isSelected = (watchedItems[index]?.taskType || []).includes(type.id as any);
                                return (
                                  <button
                                    key={type.id}
                                    type="button"
                                    onClick={() => {
                                      const current = watchedItems[index]?.taskType || [];
                                      const newTypes = current.includes(type.id as any)
                                        ? current.filter(t => t !== type.id)
                                        : [...current, type.id];
                                      setValue(`items.${index}.taskType`, newTypes as any, { shouldDirty: true });
                                      const hasManual = newTypes.includes('Manual');
                                      if (hasManual) setValue(`items.${index}.isImageRequired`, true, { shouldDirty: true });
                                    }}
                                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all duration-200 group/btn relative overflow-hidden ${isSelected
                                      ? "bg-white border-green-500 shadow-md ring-1 ring-green-500/20"
                                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                      }`}
                                  >
                                    <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${isSelected ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 group-hover/btn:bg-gray-200'
                                      }`}>
                                      <type.icon className="w-3.5 h-3.5" />
                                    </div>
                                    <span className={isSelected ? 'text-gray-900' : ''}>{type.label}</span>
                                    {isSelected && (
                                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Input Required - Yes/No Toggle */}
                          <div className="space-y-1.5">
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Input Required</label>
                            {(() => {
                              const currentValue = watchedItems[index]?.isInputRequired;
                              // Check if Yes (Both/Number/Text) or No (None)
                              const val = currentValue as any;
                              const isYes = val === 'Both' || val === 'Number' || val === 'Text';
                              const isNo = val === 'None' || !val;

                              return (
                                <>
                                  <div className="flex gap-2">
                                    {['Yes', 'No'].map((option) => {
                                      const isSelected = (option === 'Yes' && isYes) || (option === 'No' && isNo);

                                      return (
                                        <button
                                          key={option}
                                          type="button"
                                          onClick={() => {
                                            // Map Yes -> Both, No -> None for backend
                                            const mappedValue = option === 'Yes' ? 'Both' : 'None';
                                            setValue(`items.${index}.isInputRequired`, mappedValue as any, { shouldDirty: true });
                                          }}
                                          className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 border ${isSelected
                                            ? option === 'Yes'
                                              ? "bg-green-50 text-green-700 border-green-300 shadow-sm ring-1 ring-green-500/20"
                                              : "bg-gray-50 text-gray-700 border-gray-300 shadow-sm ring-1 ring-gray-500/20"
                                            : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                            }`}
                                        >
                                          {option}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <p className="text-[10px] text-gray-500 mt-1">
                                    {isYes
                                      ? 'Accepts any input (text, numbers, etc.)'
                                      : 'No input required'}
                                  </p>
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Panel 3: Execution Context */}
                        <div className="space-y-3 relative z-10">
                          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
                              <Clock className="w-3.5 h-3.5" />
                            </div>
                            <h5 className="text-xs font-bold text-gray-900">Execution Context</h5>
                          </div>

                          {/* Shift Selector */}
                          <div className="space-y-1.5">
                            <label className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Shift Schedule</label>
                            <div className="grid grid-cols-1 gap-1.5">
                              {[
                                { id: 'Morning', label: 'Pre-Gen (AM)', time: '05:00-09:00', icon: Sunrise, color: 'bg-orange-50 text-orange-600' },
                                { id: 'Standard', label: 'Standard (Day)', time: '09:00-18:00', icon: Sun, color: 'bg-yellow-50 text-yellow-600' },
                                { id: 'Evening', label: 'Post-Gen (PM)', time: '18:00-22:00', icon: Sunset, color: 'bg-indigo-50 text-indigo-600' },
                              ].map((shift) => {
                                const isSelected = watchedItems[index]?.executionPhase === shift.id;
                                return (
                                  <button
                                    key={shift.id}
                                    type="button"
                                    onClick={() => {
                                      setValue(`items.${index}.executionPhase`, shift.id, { shouldDirty: true });
                                      setValue(`items.${index}.executionTime`, shift.time, { shouldDirty: true });
                                    }}
                                    className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs border transition-all duration-200 group/shift ${isSelected
                                      ? "bg-white border-green-500 shadow-md ring-1 ring-green-500/20"
                                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                      }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`p-1.5 rounded-md transition-colors ${isSelected ? shift.color : 'bg-gray-100 text-gray-400 group-hover/shift:bg-gray-200'}`}>
                                        <shift.icon className="w-3.5 h-3.5" />
                                      </div>
                                      <span className={`font-semibold ${isSelected ? 'text-gray-900' : ''}`}>{shift.label}</span>
                                    </div>
                                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-md ${isSelected ? 'bg-gray-100 text-gray-900' : 'text-gray-400 bg-gray-50'}`}>
                                      {shift.time}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>

                            {/* Manual Time */}
                            <div className="relative mt-2">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Clock className="w-3.5 h-3.5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                placeholder="Custom Time (e.g. 09:00 - 17:00)"
                                value={watchedItems[index]?.executionTime || ''}
                                onChange={(e) => setValue(`items.${index}.executionTime`, e.target.value, { shouldDirty: true })}
                                className="w-full pl-9 pr-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all placeholder:text-gray-400"
                              />
                            </div>
                          </div>

                          {/* Evidence Toggle */}
                          <div className="pt-1">
                            <button
                              type="button"
                              onClick={() => setValue(`items.${index}.isImageRequired`, !watchedItems[index]?.isImageRequired, { shouldDirty: true })}
                              className={`flex items-center justify-between w-full px-4 py-3 rounded-xl border transition-all duration-300 group/evidence ${watchedItems[index]?.isImageRequired
                                ? "bg-gradient-to-r from-green-50 to-white border-green-200 shadow-sm"
                                : "bg-white border-gray-200 hover:border-gray-300"
                                }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`p-1.5 rounded-lg transition-colors ${watchedItems[index]?.isImageRequired ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400 group-hover/evidence:bg-gray-200"
                                  }`}>
                                  <ImageIcon className="w-4 h-4" />
                                </div>
                                <div className="text-left">
                                  <span className={`block text-xs font-bold ${watchedItems[index]?.isImageRequired ? "text-gray-900" : "text-gray-600"}`}>
                                    Evidence Required
                                  </span>
                                  <span className="block text-[10px] text-gray-400">Photo proof needed</span>
                                </div>
                              </div>
                              <div className={`w-9 h-5 rounded-full relative transition-colors duration-300 ${watchedItems[index]?.isImageRequired ? "bg-green-500" : "bg-gray-200"}`}>
                                <div className={`absolute top-1 w-3 h-3 bg-white rounded-full shadow-sm transition-all duration-300 ${watchedItems[index]?.isImageRequired ? "left-[20px]" : "left-1"}`} />
                              </div>
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Add Checkpoint Button Below List */}
              <div className="flex justify-center pt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={addCheckpoint}
                  className="font-medium h-10 px-6 text-sm border rounded-lg border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Checkpoint
                </Button>
              </div>

              {errors.items && (
                <div className="p-4 rounded-lg border"
                  style={{
                    backgroundColor: BRAND_COLORS.errorLight,
                    borderColor: `${BRAND_COLORS.error}40`
                  }}
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" style={{ color: BRAND_COLORS.error }} />
                    <p className="text-sm" style={{ color: BRAND_COLORS.error }}>
                      {errors.items.message}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* API Error - Clean Style */}
            <AnimatePresence>
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-3 p-4 rounded-lg border"
                  style={{
                    backgroundColor: BRAND_COLORS.errorLight,
                    borderColor: `${BRAND_COLORS.error}40`
                  }}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
                    style={{
                      backgroundColor: BRAND_COLORS.error + '20',
                      borderColor: BRAND_COLORS.error + '40'
                    }}
                  >
                    <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.error }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.error }}>Error</p>
                    <p className="text-xs mt-1" style={{ color: BRAND_COLORS.error }}>{apiError}</p>
                  </div>
                  <button
                    onClick={() => setApiError(null)}
                    className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
                    style={{ color: BRAND_COLORS.error }}
                  >
                    <X size={16} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form Actions - Clean Style */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleReset}
                disabled={isSubmitting}
                className="w-full sm:w-auto order-2 sm:order-1 font-medium h-10 px-6 text-sm border rounded-lg"
              >
                Reset Form
              </Button>
              <Button
                type="submit"
                variant="sorigin"
                size="lg"
                disabled={!isValid || isSubmitting || !watchedProjectTypeId || !watchedAssetId}
                className="w-full sm:w-auto min-w-[180px] order-1 sm:order-2 font-medium h-10 px-6 text-sm rounded-lg"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Checkpoints...
                  </div>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create {fields.length} Checkpoint{fields.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
} 